import AppRegistrationIcon from '@mui/icons-material/AppRegistration'
import PersonOutlineIcon from '@mui/icons-material/PersonOutline'
import TvIcon from '@mui/icons-material/Tv'
import AccountCircle from '@mui/icons-material/AccountCircle'
import Logout from '@mui/icons-material/Logout'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import CheckIcon from '@mui/icons-material/Check'
import CloseIcon from '@mui/icons-material/Close'
import {
  MenuItem,
  Select,
  Box,
  Typography,
  Container,
  Card,
  CardContent,
  Button,
  Avatar,
  IconButton,
  Menu,
  Tooltip,
  Divider,
  Chip,
  Switch,
  Checkbox,
  FormControlLabel,
  Drawer,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import { useContext, useEffect, useState, useMemo } from 'react'
import { Spinner } from 'react-bootstrap'
import { useNavigate } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import { getCPDataWithoutParams, postCPDataWithoutParams } from 'src/clinic/services'
import { saveDisplayConfigToServer } from 'src/clinic/services/displayScreenConfig'
import { saveKioskConfigToServer } from 'src/clinic/services/kioskScreenConfig'
import { SUPPORTED_LOCAL_LANGUAGES } from '../ClinicCounterScreen/oohTranslations'
import { APP_CONSTANTS } from 'src/consumer/constants'
import { getTimezoneOffset } from 'src/consumer/helpers/utilFunctions'
import { appVersion, platformWeb, mediaURL } from 'src/consumer/services'
import { ClinicDataContext } from 'src/context/ClinicDataContext'
import { logoutHandlerMentor, isValidHttpUrl, getImageLink } from 'src/mentor/helpers/utilityFunctions'
import { CAFlowSelectionDialogBox } from '../ClinicHomePage/CAHomepageComponents'
import { CAFlowSelectionDialogContinueButton } from '../ClinicHomePage/ClinicHomepageStyles'
import useAumhumScreen from '../ClinicHomePage/hooks/useAumhumScreen'
import PortalSwitcherButton from '../ClinicHeader/PortalSwitcherButton'

// Display Portal (/display, ROLE_OOH) hosts the appointment/token screen config
// and talks to /api/secure/display/*; the Kiosk Portal (/kiosk) hosts the
// remote-doctor (video) screen config and talks to /api/secure/kiosk/*.
const isDisplayPortalPath = () => window.location.pathname.startsWith('/display')
const cfgPortalScope = () => (isDisplayPortalPath() ? 'display' : 'kiosk')

// Small clipboard buttons so raw ids/slugs are never shown as bare text — the
// human-friendly title is shown; the id / slug are copy-only.
function CopyIconButton({ value, label }) {
  const [copied, setCopied] = useState(false)
  if (!value) return null
  return (
    <Tooltip title={copied ? 'Copied!' : label}>
      <IconButton
        size="small"
        sx={{ p: 0.25 }}
        onClick={(e) => {
          e.stopPropagation()
          try {
            navigator.clipboard?.writeText(String(value))
          } catch (err) {
            /* clipboard unavailable */
          }
          setCopied(true)
          setTimeout(() => setCopied(false), 1200)
        }}
      >
        {copied ? <CheckIcon sx={{ fontSize: 16 }} /> : <ContentCopyIcon sx={{ fontSize: 16 }} />}
      </IconButton>
    </Tooltip>
  )
}

// Renders a configured value as its title only, with copy-id and (optional)
// copy-slug buttons. Never shows the raw id as visible text.
function CopyableValue({ name, id, slug }) {
  const display = name && name !== id ? name : id ? 'Unnamed' : 'Not set'
  return (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
      <Typography component="span" variant="body1" sx={{ fontWeight: 600, color: '#1a1a1a' }}>
        {display}
      </Typography>
      <CopyIconButton value={id} label="Copy ID" />
      {slug && slug !== id ? <CopyIconButton value={slug} label="Copy slug" /> : null}
    </Box>
  )
}

const WALKIN_FEE_CURRENCY_PREFIX = {
  inr: 'INR ',
  usd: '$',
  eur: 'EUR ',
  gbp: 'GBP ',
}

const OOH_SCREEN_MODE_SINGLE = 'single-practitioner'
const OOH_SCREEN_MODE_TWO = 'two-practitioner'
const OOH_SCREEN_MODE_MULTI = 'multi-practitioner'

const OOH_SCREEN_MODE_OPTIONS = [
  { value: OOH_SCREEN_MODE_SINGLE, label: 'Primary only' },
  { value: OOH_SCREEN_MODE_TWO, label: 'Two practitioners' },
  { value: OOH_SCREEN_MODE_MULTI, label: 'Rotating multi' },
]

function normalizeOohScreenMode(value) {
  if (OOH_SCREEN_MODE_OPTIONS.some((option) => option.value === value)) {
    return value
  }
  return OOH_SCREEN_MODE_MULTI
}

function firstPresent(...values) {
  return values.find((value) => value !== undefined && value !== null && value !== '')
}

function formatWalkinEventFee(event) {
  const pricing = event?.pricing || {}
  const fee = firstPresent(
    event?.discountedFee,
    event?.fee,
    pricing?.discountedFee,
    pricing?.fee,
    pricing?.price,
  )
  if (fee === undefined || fee === null || fee === '') return ''

  const numericFee = Number(fee)
  const amount = Number.isFinite(numericFee)
    ? new Intl.NumberFormat('en-IN', {
        maximumFractionDigits: Number.isInteger(numericFee) ? 0 : 2,
      }).format(numericFee)
    : String(fee)
  const currencyId = String(
    firstPresent(event?.currencyId, pricing?.currencyId, event?.defaultCurrency) || '',
  ).toLowerCase()
  const prefix = firstPresent(
    event?.currencySymbol,
    pricing?.currencySymbol,
    WALKIN_FEE_CURRENCY_PREFIX[currencyId],
    currencyId ? `${currencyId.toUpperCase()} ` : '',
  ) || ''
  return `${prefix}${amount}`
}

function formatWalkinEventSource(event) {
  if (event?.sourceLabel) return event.sourceLabel
  if (event?.sourceType === 'clinic-appointment') return 'Clinic appointment'
  if (event?.sourceType === 'personal') return 'Personal'
  return ''
}

function formatWalkinEventMode(event) {
  if (event?.meetingSrcLabel) return event.meetingSrcLabel
  if (event?.meetingSrc === 'offline') return 'In-Clinic'
  if (event?.meetingSrc === 'aumhum') return 'Video Call'
  return ''
}

function formatWalkinEventOptionLabel(event) {
  return [
    event?.title,
    event?.eventType || event?.type,
    formatWalkinEventMode(event),
    formatWalkinEventFee(event),
    formatWalkinEventSource(event),
  ]
    .filter(Boolean)
    .join(' - ')
}

// Styled components for header
const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 3),
  background: 'white',
  borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
  position: 'sticky',
  top: 0,
  zIndex: 1000,
  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
}))

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  cursor: 'pointer',
  '& img': {
    height: '36px',
    width: 'auto',
    borderRadius: '8px',
  },
}))

const HeaderRight = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

const ProfileButton = styled(IconButton)(({ theme }) => ({
  width: 44,
  height: 44,
  borderRadius: '12px',
  padding: 2,
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  border: '2px solid transparent',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(116, 80, 170, 0.25)',
    borderColor: 'rgba(116, 80, 170, 0.2)',
  },
}))

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: '100%',
  height: '100%',
  background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
  border: '2px solid rgba(255, 255, 255, 0.9)',
  boxShadow: '0 2px 8px rgba(116, 80, 170, 0.3)',
  fontWeight: 700,
  fontSize: 16,
}))

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '16px',
    marginTop: 8,
    minWidth: 280,
    maxWidth: 320,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12), 0 2px 8px rgba(0, 0, 0, 0.08)',
    border: '1px solid rgba(116, 80, 170, 0.1)',
  },
}))

const MenuItemStyled = styled(MenuItem)(({ theme }) => ({
  padding: '12px 20px',
  borderRadius: '10px',
  margin: '4px 8px',
  transition: 'all 0.2s ease',
  '&:hover': {
    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.08) 0%, rgba(116, 80, 170, 0.04) 100%)',
  },
}))

// Styled components for config card
const ConfigCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(4),
  borderRadius: '20px',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
  border: '2px solid rgba(116, 80, 170, 0.15)',
  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.02) 0%, rgba(255, 255, 255, 1) 100%)',
}))

const ConfigCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
}))

const ConfigTitle = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 700,
  color: '#1f2937',
  marginBottom: theme.spacing(1),
}))

const ConfigSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#6b7280',
  marginBottom: theme.spacing(3),
}))

const ConfigDetailRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(0.5),
  marginBottom: theme.spacing(1.5),
  '&:last-child': {
    marginBottom: 0,
  },
}))

const ConfigLabel = styled(Typography)(({ theme }) => ({
  fontSize: '12px',
  fontWeight: 600,
  color: '#6b7280',
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
}))

const ConfigValue = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 500,
  color: '#1f2937',
}))

const ActionButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(2),
  marginTop: theme.spacing(3),
  justifyContent: 'flex-end',
}))

const ActionButton = styled(Button)(({ theme, variant }) => ({
  borderRadius: '12px',
  padding: theme.spacing(1.25, 3),
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '14px',
  ...(variant === 'outlined'
    ? {
        borderColor: '#7450aa',
        color: '#7450aa',
        '&:hover': {
          borderColor: '#5a3d88',
          background: 'rgba(116, 80, 170, 0.05)',
        },
      }
    : {
        background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
        color: 'white',
        '&:hover': {
          background: 'linear-gradient(135deg, #5a3d88 0%, #4a2d6a 100%)',
        },
      }),
}))

// Modern card-based screen type selection
const ScreenTypeCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'selected',
})(({ theme, selected }) => ({
  cursor: 'pointer',
  borderRadius: '20px',
  border: selected
    ? '2px solid #7450aa'
    : '2px solid rgba(0, 0, 0, 0.08)',
  background: selected
    ? 'linear-gradient(135deg, rgba(116, 80, 170, 0.05) 0%, rgba(116, 80, 170, 0.02) 100%)'
    : 'white',
  boxShadow: selected
    ? '0 8px 24px rgba(116, 80, 170, 0.15)'
    : '0 2px 8px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  overflow: 'hidden',
  position: 'relative',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: selected
      ? '0 12px 32px rgba(116, 80, 170, 0.2)'
      : '0 8px 20px rgba(0, 0, 0, 0.12)',
    borderColor: selected ? '#7450aa' : 'rgba(116, 80, 170, 0.3)',
  },
  '&::before': selected
    ? {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: 'linear-gradient(90deg, #7450aa 0%, #8b6fd8 100%)',
      }
    : {},
}))

const ScreenTypeCardContent = styled(CardContent)(({ theme }) => ({
  padding: theme.spacing(3),
  '&:last-child': {
    paddingBottom: theme.spacing(3),
  },
}))

const IconContainer = styled(Box)(({ theme }) => ({
  width: 64,
  height: 64,
  borderRadius: '16px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.1) 0%, rgba(116, 80, 170, 0.05) 100%)',
  color: '#7450aa',
}))

const ScreenTypeTitle = styled(Typography)(({ theme }) => ({
  fontSize: '20px',
  fontWeight: 700,
  color: '#1f2937',
  marginBottom: theme.spacing(1),
}))

const ScreenTypeDescription = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  color: '#6b7280',
  lineHeight: 1.6,
}))

const FormFieldContainer = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(3),
}))

const FormLabel = styled(Typography)(({ theme }) => ({
  fontSize: '14px',
  fontWeight: 600,
  color: '#374151',
  marginBottom: theme.spacing(1),
}))

const StyledSelect = styled(Select)(({ theme }) => ({
  borderRadius: '12px',
  '& .MuiOutlinedInput-notchedOutline': {
    borderColor: '#d1d5db',
  },
  '&:hover .MuiOutlinedInput-notchedOutline': {
    borderColor: '#9ca3af',
  },
  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
    borderColor: '#7450aa',
    borderWidth: '2px',
  },
}))

// Walk-in QR booking config for the selected mentor at the selected location.
// Uses the secure display/kiosk scope so clinic-affiliated practitioners do not
// need to be globally public-listable just to configure an in-clinic screen.
const WalkinScreenConfigSection = ({ clinicLocationId, mentorId, showTitle = true }) => {
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [savedMsg, setSavedMsg] = useState('')
  const [enabled, setEnabled] = useState(false)
  const [offlineEvents, setOfflineEvents] = useState([])
  const [defaultEventId, setDefaultEventId] = useState('')
  const [specialEventIds, setSpecialEventIds] = useState([])
  const [displayLanguage, setDisplayLanguage] = useState('') // '' = English only
  const [announcementsEnabled, setAnnouncementsEnabled] = useState(true)
  const [qrAlwaysVisible, setQrAlwaysVisible] = useState(false)
  const [screenMode, setScreenMode] = useState(OOH_SCREEN_MODE_MULTI)

  useEffect(() => {
    let cancelled = false
    async function load() {
      if (!clinicLocationId || !mentorId) return
      setLoading(true)
      setSavedMsg('')
      setEnabled(false)
      setOfflineEvents([])
      setDefaultEventId('')
      setSpecialEventIds([])
      setDisplayLanguage('')
      setAnnouncementsEnabled(true)
      setQrAlwaysVisible(false)
      setScreenMode(OOH_SCREEN_MODE_MULTI)
      try {
        // Existing walk-in config (prefill).
        const cfgRes = await getCPDataWithoutParams(
          `/api/secure/${cfgPortalScope()}/ooh/config/get?mentorId=${mentorId}&clinicLocationId=${clinicLocationId}`,
        )
        const cfg = cfgRes?.data?.data?.['ooh-screen-config'] || cfgRes?.data?.['ooh-screen-config']
        if (!cancelled && cfg) {
          setEnabled(Boolean(cfg.walkinBookingEnabled))
          setDefaultEventId(cfg.walkinDefaultEventId || '')
          setSpecialEventIds(Array.isArray(cfg.walkinSpecialEventIds) ? cfg.walkinSpecialEventIds : [])
          setDisplayLanguage(cfg.displayLanguage || '')
          // Default true: legacy rows without the field keep announcements on.
          setAnnouncementsEnabled(cfg.announcementsEnabled !== false)
          setQrAlwaysVisible(Boolean(cfg.walkinQrAlwaysVisible))
          setScreenMode(normalizeOohScreenMode(cfg.screenMode))
        }
        // Bookable events for this mentor at this clinic.
        const eventsRes = await getCPDataWithoutParams(
          `/api/secure/${cfgPortalScope()}/walkin/bookable-events?mentorId=${encodeURIComponent(
            mentorId,
          )}&clinicLocationId=${encodeURIComponent(clinicLocationId)}`,
        )
        const events = eventsRes?.data?.data?.events || eventsRes?.data?.events
        if (!cancelled) setOfflineEvents(Array.isArray(events) ? events : [])
      } catch (e) {
        console.error('Error loading walk-in config:', e)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [clinicLocationId, mentorId])

  async function handleSave() {
    setSaving(true)
    setSavedMsg('')
    try {
      const res = await postCPDataWithoutParams(`/api/secure/${cfgPortalScope()}/walkin/config`, {
        mentorId,
        clinicLocationId,
        enabled,
        defaultEventId: enabled ? defaultEventId : '',
        specialEventIds: enabled ? specialEventIds.filter((id) => id !== defaultEventId) : [],
        displayLanguage: displayLanguage || '',
        announcementsEnabled,
        qrAlwaysVisible: enabled ? qrAlwaysVisible : false,
        screenMode,
      })
      setSavedMsg(res?.status === 200 ? 'Screen settings saved.' : 'Could not save. Please retry.')
    } catch (e) {
      console.error('Error saving walk-in config:', e)
      setSavedMsg('Could not save. Please retry.')
    } finally {
      setSaving(false)
    }
  }

  if (!clinicLocationId || !mentorId) return null

  return (
    <Box
      sx={{
        mt: showTitle ? 3 : 0,
        p: showTitle ? 2 : 0,
        border: showTitle ? '1px solid #e5e7eb' : 'none',
        borderRadius: 2,
      }}
    >
      {showTitle && (
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
          OOH Screen Settings
        </Typography>
      )}
      {loading ? (
        <Spinner size="sm" variant="dark" />
      ) : (
        <>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.75 }}>
              Screen mode
            </Typography>
            <ToggleButtonGroup
              exclusive
              value={screenMode}
              onChange={(_, value) => {
                if (value) setScreenMode(value)
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
                gap: 1,
                width: '100%',
                '& .MuiToggleButtonGroup-grouped': {
                  border: '1px solid #d1d5db !important',
                  borderRadius: '10px !important',
                  mx: '0 !important',
                  textTransform: 'none',
                  fontWeight: 600,
                  px: 1,
                  py: 1,
                  color: '#374151',
                  '&.Mui-selected': {
                    bgcolor: 'rgba(116, 80, 170, 0.12)',
                    color: '#5a3d88',
                    borderColor: '#7450aa !important',
                  },
                },
              }}
            >
              {OOH_SCREEN_MODE_OPTIONS.map((option) => (
                <ToggleButton key={option.value} value={option.value}>
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5 }}>
              Local language (shown alongside English)
            </Typography>
            <Select
              fullWidth
              displayEmpty
              value={displayLanguage || ''}
              onChange={(e) => setDisplayLanguage(e.target.value)}
            >
              <MenuItem value="">
                <em>English only</em>
              </MenuItem>
              {SUPPORTED_LOCAL_LANGUAGES.map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  {lang.label}
                </MenuItem>
              ))}
            </Select>
          </Box>
          <FormControlLabel
            control={
              <Switch
                checked={announcementsEnabled}
                onChange={(e) => setAnnouncementsEnabled(e.target.checked)}
              />
            }
            label="Audio announcements (call patient by token over speakers)"
          />
          <FormControlLabel
            control={<Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />}
            label="Show a booking QR on this screen"
          />
          {enabled && (
            <Box sx={{ mt: 1 }}>
              {offlineEvents.length === 0 ? (
                <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic' }}>
                  No bookable sessions found for this practitioner at this location.
                </Typography>
              ) : (
                <>
                  <Typography variant="body2" sx={{ mt: 1, mb: 0.5 }}>
                    Default session (the QR books this)
                  </Typography>
                  <Select
                    fullWidth
                    displayEmpty
                    value={defaultEventId || ''}
                    onChange={(e) => setDefaultEventId(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Select a session</em>
                    </MenuItem>
                    {offlineEvents.map((ev) => (
                      <MenuItem key={ev?.uid} value={ev?.uid}>
                        {formatWalkinEventOptionLabel(ev)}
                      </MenuItem>
                    ))}
                  </Select>
                  <Typography variant="body2" sx={{ mt: 2, mb: 0.5 }}>
                    Special sessions the patient may also pick (optional)
                  </Typography>
                  {offlineEvents
                    .filter((ev) => ev?.uid !== defaultEventId)
                    .map((ev) => (
                      <FormControlLabel
                        key={ev?.uid}
                        control={
                          <Checkbox
                            checked={specialEventIds.includes(ev?.uid)}
                            onChange={(e) => {
                              setSpecialEventIds((prev) =>
                                e.target.checked
                                  ? [...prev, ev.uid]
                                  : prev.filter((id) => id !== ev.uid),
                              )
                            }}
                          />
                        }
                        label={formatWalkinEventOptionLabel(ev)}
                      />
                    ))}
                </>
              )}
              <Box sx={{ mt: 1.5 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={qrAlwaysVisible}
                      onChange={(e) => setQrAlwaysVisible(e.target.checked)}
                    />
                  }
                  label="Keep QR always visible (don't rotate it with other messages)"
                />
                <Typography variant="caption" sx={{ display: 'block', color: '#6b7280', ml: 6, mt: -0.5 }}>
                  When off, the QR appears as one rotating slide (~15s at a time) alongside other
                  display messages. When on, it stays pinned in its own card at all times.
                </Typography>
              </Box>
            </Box>
          )}
          <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={saving || (enabled && !defaultEventId)}
            >
              {saving ? 'Saving…' : 'Save screen settings'}
            </Button>
            {savedMsg && (
              <Typography variant="body2" sx={{ color: '#374151' }}>
                {savedMsg}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

// Separate component for appointment screen configuration
const AppointmentScreenConfig = ({
  loading,
  selectedBrand,
  selectedLocation,
  selectedMentor,
  onBrandChange,
  onLocationChange,
  onMentorChange,
  brands,
  locations,
  mentors,
  mentorLocations, // API 3: mentor-location mapping
}) => {
  const [settingsOpen, setSettingsOpen] = useState(false)
  const selectedMentorData = useMemo(
    () => mentors?.find((mentor) => mentor?.id === selectedMentor),
    [mentors, selectedMentor],
  )
  const selectedMentorName = selectedMentorData?.name || selectedMentorData?.firstName || 'Selected practitioner'

  useEffect(() => {
    if (selectedLocation && selectedMentor) {
      setSettingsOpen(true)
    } else {
      setSettingsOpen(false)
    }
  }, [selectedLocation, selectedMentor])

  if (loading) return <Spinner size="sm" variant="dark" />

  return (
    <Box>
      <FormFieldContainer>
        <FormLabel>Clinic Brand</FormLabel>
        <StyledSelect
          fullWidth
          displayEmpty
          value={selectedBrand || ''}
          onChange={(e) => onBrandChange(e.target.value)}
        >
          <MenuItem value="">
            <em>Select a clinic brand</em>
          </MenuItem>
          {brands?.map((item) => (
            <MenuItem key={item?.clinicBrandId} value={item?.clinicBrandId}>
              {item?.clinicBrandName}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormFieldContainer>

      <FormFieldContainer>
        <FormLabel>Clinic Location</FormLabel>
        <StyledSelect
          fullWidth
          displayEmpty
          value={selectedLocation || ''}
          onChange={(e) => onLocationChange(e.target.value)}
          disabled={!selectedBrand}
        >
          <MenuItem value="">
            <em>Select a clinic location</em>
          </MenuItem>
          {locations
            ?.filter((item) => item?.clinicBrandId === selectedBrand)
            ?.map((item) => (
              <MenuItem key={item?.id} value={item?.id}>
                {item?.name}
              </MenuItem>
            ))}
        </StyledSelect>
      </FormFieldContainer>

      <FormFieldContainer>
        <FormLabel>Select Doctor / Therapist</FormLabel>
        {!selectedLocation ? (
          <Typography variant="body2" sx={{ color: '#9ca3af', fontStyle: 'italic', mt: 1 }}>
            Please select a clinic location first
          </Typography>
        ) : (
          <Box sx={{ mt: 1 }}>
            {(() => {
              // Filter mentors available for this location using mentorLocations from API 3
              const availableMentors = mentors?.filter((mentor) => {
                const locationIds = mentorLocations[mentor?.id] || []
                return locationIds.includes(selectedLocation)
              }) || []

              // Deduplicate by mentor ID (in case same mentor appears multiple times)
              const uniqueMentorsMap = new Map()
              availableMentors.forEach((manager) => {
                if (manager?.id && !uniqueMentorsMap.has(manager.id)) {
                  uniqueMentorsMap.set(manager.id, manager)
                }
              })
              const uniqueMentors = Array.from(uniqueMentorsMap.values())

              // Helper function to get mentor role
              const getMentorRole = (categories) => {
                if (!categories || !Array.isArray(categories)) return 'Practitioner'
                if (categories.includes('doctor')) return 'Doctor'
                if (categories.includes('therapist')) return 'Therapist'
                if (categories.includes('screener')) return 'Screener'
                return 'Practitioner'
              }

              // Separate mentors by role
              const doctors = uniqueMentors.filter((manager) => getMentorRole(manager?.categories) === 'Doctor')
              const therapists = uniqueMentors.filter((manager) => getMentorRole(manager?.categories) === 'Therapist')
              const others = uniqueMentors.filter(
                (manager) => !['Doctor', 'Therapist'].includes(getMentorRole(manager?.categories)),
              )

              // Render mentor card component
              const renderMentorCard = (manager) => {
                const role = getMentorRole(manager?.categories)
                const isSelected = selectedMentor === manager?.id

                // Get profile picture URL
                const profilePicId = manager?.profilePicId || manager?.profilePictureId
                let profilePicUrl = null
                if (profilePicId) {
                  if (isValidHttpUrl(profilePicId)) {
                    profilePicUrl = profilePicId
                  } else {
                    profilePicUrl = getImageLink(profilePicId)
                  }
                }

                // Get initials for fallback
                const getInitials = (name) => {
                  if (!name) return '?'
                  const words = name.trim().split(/\s+/).filter((w) => w.length > 0)
                  if (words.length > 1) {
                    return (words[0][0] + words[words.length - 1][0]).toUpperCase()
                  } else if (words.length === 1 && words[0].length > 0) {
                    return words[0].substring(0, 2).toUpperCase()
                  }
                  return '?'
                }

                const mentorName = manager?.name || manager?.firstName || 'Unknown'
                const initials = getInitials(mentorName)

                return (
                  <Card
                    key={manager?.id}
                    onClick={() => {
                      onMentorChange(manager?.id)
                      setSettingsOpen(true)
                    }}
                    sx={{
                      cursor: 'pointer',
                      border: isSelected ? '2px solid #7450aa' : '1px solid #e5e7eb',
                      backgroundColor: isSelected ? 'rgba(116, 80, 170, 0.05)' : 'white',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: '0 4px 12px rgba(116, 80, 170, 0.15)',
                        borderColor: '#7450aa',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
                        <Avatar
                          src={profilePicUrl || undefined}
                          sx={{
                            width: 40,
                            height: 40,
                            bgcolor: isSelected ? '#7450aa' : '#9ca3af',
                            fontSize: 16,
                            fontWeight: 600,
                          }}
                        >
                          {!profilePicUrl && initials}
                        </Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: '#1f2937',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {mentorName}
                          </Typography>
                          <Chip
                            label={role}
                            size="small"
                            sx={{
                              height: 20,
                              fontSize: '10px',
                              fontWeight: 600,
                              bgcolor: isSelected ? '#7450aa' : '#f3f4f6',
                              color: isSelected ? 'white' : '#6b7280',
                              mt: 0.5,
                            }}
                          />
                        </Box>
                        {isSelected && (
                          <Box
                            sx={{
                              width: 24,
                              height: 24,
                              borderRadius: '50%',
                              bgcolor: '#7450aa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: 14,
                              fontWeight: 600,
                            }}
                          >
                            ✓
                          </Box>
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                )
              }

              return (
                <>
                  {/* Doctors Section */}
                  {doctors.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: '#374151',
                          mb: 2,
                          fontSize: '16px',
                        }}
                      >
                        Doctors
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                          gap: 2,
                        }}
                      >
                        {doctors.map(renderMentorCard)}
                      </Box>
                    </Box>
                  )}

                  {/* Therapists Section */}
                  {therapists.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: '#374151',
                          mb: 2,
                          fontSize: '16px',
                        }}
                      >
                        Therapists
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                          gap: 2,
                        }}
                      >
                        {therapists.map(renderMentorCard)}
                      </Box>
                    </Box>
                  )}

                  {/* Other Practitioners Section */}
                  {others.length > 0 && (
                    <Box>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 600,
                          color: '#374151',
                          mb: 2,
                          fontSize: '16px',
                        }}
                      >
                        Other Practitioners
                      </Typography>
                      <Box
                        sx={{
                          display: 'grid',
                          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                          gap: 2,
                        }}
                      >
                        {others.map(renderMentorCard)}
                      </Box>
                    </Box>
                  )}
                </>
              )
            })()}
          </Box>
        )}
      </FormFieldContainer>

      {selectedLocation && selectedMentor && (
        <Drawer
          anchor="right"
          open={settingsOpen}
          onClose={() => setSettingsOpen(false)}
          PaperProps={{
            sx: {
              width: { xs: '100%', sm: 520 },
              maxWidth: '100vw',
            },
          }}
        >
          <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1f2937' }}>
                  OOH Screen Settings
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: '#6b7280',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: '100%',
                  }}
                >
                  {selectedMentorName}
                </Typography>
              </Box>
              <IconButton onClick={() => setSettingsOpen(false)} aria-label="Close settings">
                <CloseIcon />
              </IconButton>
            </Box>
            <Divider sx={{ my: 2 }} />
            <WalkinScreenConfigSection
              clinicLocationId={selectedLocation}
              mentorId={selectedMentor}
              showTitle={false}
            />
          </Box>
        </Drawer>
      )}
    </Box>
  )
}

// Separate component for remote doctor screen configuration
const RemoteDoctorConfig = ({
  loading,
  selectedScreenConfig,
  selectedVirtualRoom,
  onScreenConfigChange,
  onVirtualRoomChange,
  screenConfigs,
  virtualConfigs,
}) => {
  if (loading) return <Spinner variant="dark" size="sm" />

  return (
    <Box>
      <FormFieldContainer>
        <FormLabel>Screen Device Configuration</FormLabel>
        <StyledSelect
          fullWidth
          value={selectedScreenConfig}
          onChange={(e) => onScreenConfigChange(e.target.value)}
        >
          {screenConfigs?.map((item) => (
            <MenuItem key={item?.id} value={item?.id}>
              {item?.title}
            </MenuItem>
          ))}
        </StyledSelect>
      </FormFieldContainer>

      <FormFieldContainer>
        <FormLabel>
          <span
            style={{
              display: 'inline-block',
              background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.1) 100%)',
              color: '#7450aa',
              fontWeight: 700,
              padding: '4px 12px',
              borderRadius: '20px',
              border: '1px solid rgba(116, 80, 170, 0.2)',
            }}
          >
            Chamber Room
          </span>
        </FormLabel>
        <StyledSelect
          fullWidth
          value={selectedVirtualRoom}
          onChange={(e) => onVirtualRoomChange(e.target.value)}
          disabled={!selectedScreenConfig}
        >
              {virtualConfigs
            ?.filter((item) => item?.screenDeviceConfigId === selectedScreenConfig)
            ?.map((item) => (
              <MenuItem key={item?.virtualChamberRoomId} value={item?.virtualChamberRoomId}>
                {item?.displayName || item?.title || item?.virtualChamberRoomId}
              </MenuItem>
            ))}
        </StyledSelect>
      </FormFieldContainer>
    </Box>
  )
}

// Component to display current config
const CurrentConfigCard = ({ config, brands, locations, mentors, screenConfigs, virtualConfigs, onChange, onLeave }) => {
  const getFlowTypeName = (flowId) => {
    if (flowId === 'appointment-screen') return 'Appointment Number Screen'
    if (flowId === 'remote-doctor') return 'Video Call Screen'
    return 'Unknown'
  }

  const getBrandName = (brandId) => {
    const brand = brands?.find((b) => b?.clinicBrandId === brandId)
    return brand?.clinicBrandName || brandId
  }

  const getBrandSlug = (brandId) => {
    const brand = brands?.find((b) => b?.clinicBrandId === brandId)
    return brand?.clinicBrandSlug || ''
  }

  const getLocationName = (locationId) => {
    const location = locations?.find((l) => l?.id === locationId)
    return location?.name || locationId
  }

  const getLocationSlug = (locationId) => {
    const location = locations?.find((l) => l?.id === locationId)
    return location?.slug || locationId
  }

  const getMentorName = (mentorId) => {
    const mentor = mentors?.find((m) => m?.id === mentorId)
    return mentor?.name || mentor?.firstName || mentorId
  }

  const getMentorSlug = (mentorId) => {
    const mentor = mentors?.find((m) => m?.id === mentorId)
    // Check both slug and uid (some APIs return slug as uid)
    return mentor?.slug || mentor?.uid || mentorId
  }

  const getMentorRole = (mentorId) => {
    const mentor = mentors?.find((m) => m?.id === mentorId)
    if (!mentor?.categories || !Array.isArray(mentor.categories)) return 'Practitioner'
    if (mentor.categories.includes('doctor')) return 'Doctor'
    if (mentor.categories.includes('therapist')) return 'Therapist'
    if (mentor.categories.includes('screener')) return 'Screener'
    return 'Practitioner'
  }

  const getScreenConfigName = (configId) => {
    if (!configId) return null
    const config = screenConfigs?.find((c) => c?.id === configId)
    return config?.title || null // Return null if not found, so we can show ID separately
  }

  const getVirtualRoomName = (roomId) => {
    if (!roomId) return null
    const filteredConfigs = virtualConfigs?.filter(
      (item) => item?.screenDeviceConfigId === config?.SCREEN_DEVICE_CONFIG_ID,
    )
    const room = filteredConfigs?.find((r) => r?.virtualChamberRoomId === roomId)
    if (room) {
      // API 1 already includes pre-formatted displayName: "Title (Brand)"
      return room?.displayName || room?.title || null
    }
    return null // Return null if not found, so we can show ID separately
  }

  return (
    <ConfigCard>
      <ConfigCardContent>
        <ConfigTitle>Current Configuration</ConfigTitle>
        <ConfigSubtitle>
          {getFlowTypeName(isDisplayPortalPath() ? 'appointment-screen' : 'remote-doctor')}
        </ConfigSubtitle>

        {isDisplayPortalPath() && (
          <>
            <ConfigDetailRow>
              <ConfigLabel>Clinic Brand</ConfigLabel>
              <ConfigValue component="div">
                <CopyableValue
                  name={getBrandName(config?.BRAND_FOR_OOH)}
                  id={config?.BRAND_FOR_OOH}
                  slug={getBrandSlug(config?.BRAND_FOR_OOH)}
                />
              </ConfigValue>
            </ConfigDetailRow>
            <ConfigDetailRow>
              <ConfigLabel>Clinic Location</ConfigLabel>
              <ConfigValue component="div">
                <CopyableValue
                  name={getLocationName(config?.LOCATION_FOR_OOH)}
                  id={config?.LOCATION_FOR_OOH}
                  slug={getLocationSlug(config?.LOCATION_FOR_OOH)}
                />
              </ConfigValue>
            </ConfigDetailRow>
            <ConfigDetailRow>
              <ConfigLabel>Doctor/Therapist</ConfigLabel>
              <ConfigValue component="div" sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                <CopyableValue
                  name={getMentorName(config?.MENTOR_FOR_OOH)}
                  id={config?.MENTOR_FOR_OOH}
                  slug={getMentorSlug(config?.MENTOR_FOR_OOH)}
                />
                <Chip
                  label={getMentorRole(config?.MENTOR_FOR_OOH)}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: '11px',
                    fontWeight: 600,
                    bgcolor: '#7450aa',
                    color: 'white',
                  }}
                />
              </ConfigValue>
            </ConfigDetailRow>
          </>
        )}

        {!isDisplayPortalPath() && (
          <>
            <ConfigDetailRow>
              <ConfigLabel>Screen Device Config</ConfigLabel>
              <ConfigValue component="div">
                <CopyableValue
                  name={getScreenConfigName(config?.SCREEN_DEVICE_CONFIG_ID)}
                  id={config?.SCREEN_DEVICE_CONFIG_ID}
                />
              </ConfigValue>
            </ConfigDetailRow>
            <ConfigDetailRow>
              <ConfigLabel>
                <span
                  style={{
                    display: 'inline-block',
                    background: 'linear-gradient(135deg, rgba(116, 80, 170, 0.15) 0%, rgba(116, 80, 170, 0.1) 100%)',
                    color: '#7450aa',
                    fontWeight: 700,
                    padding: '4px 12px',
                    borderRadius: '20px',
                    border: '1px solid rgba(116, 80, 170, 0.2)',
                  }}
                >
                  Chamber Room
                </span>
              </ConfigLabel>
              <ConfigValue component="div">
                <CopyableValue
                  name={getVirtualRoomName(config?.CURRENT_VIRTUAL_ROOM_ID)}
                  id={config?.CURRENT_VIRTUAL_ROOM_ID}
                />
              </ConfigValue>
            </ConfigDetailRow>
          </>
        )}

        <ActionButtonsContainer>
          <ActionButton variant="outlined" onClick={onLeave}>
            Continue
          </ActionButton>
          <ActionButton variant="contained" onClick={onChange}>
            Change
          </ActionButton>
        </ActionButtonsContainer>
      </ConfigCardContent>
    </ConfigCard>
  )
}

// Custom hook for data management - Uses new Kiosk APIs
const useKioskDeviceData = () => {
  const [oohDataLoading, setOOHDataLoading] = useState(false)
  const [screenConfigLoading, setScreenConfigLoading] = useState(false)
  const [brands, setBrands] = useState([])
  const [locations, setLocations] = useState([])
  const [mentors, setMentors] = useState([])
  const [mentorLocations, setMentorLocations] = useState({}) // API 3: mentor-location mapping
  const [screenConfigs, setScreenConfigs] = useState([])
  const [virtualConfigs, setVirtualConfigs] = useState([])

  // API 2: Get clinics (brands and locations)
  const setupOOHData = async () => {
    setOOHDataLoading(true)
    try {
      const response = await getCPDataWithoutParams(`/api/secure/${cfgPortalScope()}/clinics`)
      if (response?.status === 200 && response?.data?.code === 2000) {
        const brandsData = response?.data?.data?.brands || []
        
        // Extract brands (already in hierarchical structure)
        const extractedBrands = brandsData.map((brand) => ({
          clinicBrandId: brand?.clinicBrandId,
          clinicBrandSlug: brand?.clinicBrandSlug,
          clinicBrandName: brand?.clinicBrandName,
          clinicBrandShortName: brand?.clinicBrandShortName,
          whatsappSupportQrCodeMediaId: brand?.whatsappSupportQrCodeMediaId,
        }))
        
        // Extract all locations from all brands (flatten for backward compatibility)
        const allLocations = []
        brandsData.forEach((brand) => {
          if (Array.isArray(brand?.locations)) {
            allLocations.push(...brand.locations)
          }
        })
        
        setBrands(extractedBrands)
        setLocations(allLocations)
      } else {
        console.error('Error fetching clinics:', response?.data?.message)
      }
    } catch (error) {
      console.error('Error fetching clinics:', error)
    } finally {
      setOOHDataLoading(false)
    }
  }

  // API 3: Get mentors
  const setupMentorsData = async () => {
    try {
      const response = await getCPDataWithoutParams(`/api/secure/${cfgPortalScope()}/mentors`)
      if (response?.status === 200 && response?.data?.code === 2000) {
        const mentorsData = response?.data?.data?.mentors || []
        const mentorLocationsMap = response?.data?.data?.mentorLocations || {}
        setMentors(mentorsData)
        setMentorLocations(mentorLocationsMap)
      } else {
        console.error('Error fetching mentors:', response?.data?.message)
      }
    } catch (error) {
      console.error('Error fetching mentors:', error)
    }
  }

  // API 1: Get screen device configs
  const setupDoctorScreenData = async () => {
    setScreenConfigLoading(true)
    try {
      const response = await getCPDataWithoutParams('/api/secure/kiosk/screen-device-configs')
      if (response?.status === 200 && response?.data?.code === 2000) {
        const screenDeviceConfigs = response?.data?.data?.screenDeviceConfigs || []
        const virtualChamberRooms = response?.data?.data?.virtualChamberRooms || []
        
        setScreenConfigs(screenDeviceConfigs)
        setVirtualConfigs(virtualChamberRooms)
      } else {
        console.error('Error fetching screen device configs:', response?.data?.message)
      }
    } catch (error) {
      console.error('Error fetching screen device configs:', error)
    } finally {
      setScreenConfigLoading(false)
    }
  }

  useEffect(() => {
    // Portal separation: the Display Portal config only needs OOH catalog data
    // (clinics + mentors); the Kiosk Portal config only needs screen-device
    // configs. Crucially, screen-device-configs is ROLE_KIOSK-gated, so a
    // Display (ROLE_OOH) user must NOT call it.
    if (isDisplayPortalPath()) {
      setupOOHData()
      setupMentorsData()
    } else {
      setupDoctorScreenData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    oohDataLoading,
    screenConfigLoading,
    brands,
    locations,
    mentors,
    mentorLocations, // API 3: mentor-location mapping for filtering
    screenConfigs,
    virtualConfigs,
  }
}

// Main Kiosk Select Flow Page Component
export default function KioskSelectFlowPage() {
  const navigate = useNavigate()
  const { assistantData } = useContext(ClinicDataContext)
  const { registerScreen, unregisterScreen } = useAumhumScreen()

  // Get config - use kiosk-config with fallback to ROLE_CA_CONFIG
  const kioskConfigStr = localStorage.getItem('kiosk-config')
  const roleCaConfigStr = localStorage.getItem('ROLE_CA_CONFIG')
  const kioskConfig = kioskConfigStr ? JSON.parse(kioskConfigStr) : (roleCaConfigStr ? JSON.parse(roleCaConfigStr) : {})
  const fallbackConfig = roleCaConfigStr ? JSON.parse(roleCaConfigStr) : {}

  // A valid existing config is determined by the portal's target fields being
  // present (no stored flow field). Display = location+mentor; Kiosk = screen
  // device + chamber room.
  const hasValidConfig = isDisplayPortalPath()
    ? Boolean(kioskConfig?.LOCATION_FOR_OOH && kioskConfig?.MENTOR_FOR_OOH)
    : Boolean(kioskConfig?.SCREEN_DEVICE_CONFIG_ID && kioskConfig?.CURRENT_VIRTUAL_ROOM_ID)

  // Show config form when a screen type is selected
  const [showConfigForm, setShowConfigForm] = useState(false)
  // Track if user clicked "Change" to show flow selection
  const [showFlowSelection, setShowFlowSelection] = useState(false)

  // Each portal owns exactly one flow (Display = appointment/token,
  // Kiosk = video call), so the flow is fixed by the portal — there is no
  // selectable flow field anymore.
  const selectedFlow = isDisplayPortalPath() ? 'appointment-screen' : 'remote-doctor'

  // Appointment screen states
  const [selectedBrandForOOH, setSelectedBrandForOOH] = useState(
    kioskConfig?.BRAND_FOR_OOH || fallbackConfig?.BRAND_FOR_OOH || '',
  )
  const [selectedLocationForOOH, setSelectedLocationForOOH] = useState(
    kioskConfig?.LOCATION_FOR_OOH || fallbackConfig?.LOCATION_FOR_OOH || '',
  )
  const [selectedMentorForOOH, setSelectedMentorForOOH] = useState(
    kioskConfig?.MENTOR_FOR_OOH || fallbackConfig?.MENTOR_FOR_OOH || '',
  )

  // Remote doctor states - initialize as empty, will be set once data loads
  const [selectedScreenConfigForRemoteScreen, setSelectedScreenConfigForRemoteScreen] = useState('')
  const [selectedCurrentVirtualRoomId, setSelectedCurrentVirtualRoomId] = useState('')

  const [isRegisteringScreen, setIsRegisteringScreen] = useState(false)

  // Profile menu state
  const [anchorEl, setAnchorEl] = useState(null)
  const open = Boolean(anchorEl)

  const {
    oohDataLoading,
    screenConfigLoading,
    brands,
    locations,
    mentors,
    mentorLocations,
    screenConfigs,
    virtualConfigs,
  } = useKioskDeviceData()

  // Set initial values from config once data loads
  useEffect(() => {
    if (!screenConfigLoading && screenConfigs.length > 0 && !selectedScreenConfigForRemoteScreen) {
      const savedScreenConfig = kioskConfig?.SCREEN_DEVICE_CONFIG_ID || fallbackConfig?.SCREEN_DEVICE_CONFIG_ID
      if (savedScreenConfig) {
        const isValidScreenConfig = screenConfigs.some((config) => config.id === savedScreenConfig)
        if (isValidScreenConfig) {
          setSelectedScreenConfigForRemoteScreen(savedScreenConfig)
        }
      }
    }
  }, [screenConfigs, screenConfigLoading, kioskConfig, fallbackConfig, selectedScreenConfigForRemoteScreen])

  useEffect(() => {
    if (
      !screenConfigLoading &&
      virtualConfigs.length > 0 &&
      selectedScreenConfigForRemoteScreen &&
      !selectedCurrentVirtualRoomId
    ) {
      const savedVirtualRoom = kioskConfig?.CURRENT_VIRTUAL_ROOM_ID || fallbackConfig?.CURRENT_VIRTUAL_ROOM_ID
      if (savedVirtualRoom) {
        const filteredVirtualConfigs = virtualConfigs.filter(
          (item) => item?.screenDeviceConfigId === selectedScreenConfigForRemoteScreen,
        )
        const isValidVirtualRoom = filteredVirtualConfigs.some(
          (config) => config.virtualChamberRoomId === savedVirtualRoom,
        )
        if (isValidVirtualRoom) {
          setSelectedCurrentVirtualRoomId(savedVirtualRoom)
        }
      }
    }
  }, [
    virtualConfigs,
    screenConfigLoading,
    selectedScreenConfigForRemoteScreen,
    kioskConfig,
    fallbackConfig,
    selectedCurrentVirtualRoomId,
  ])

  // Clear location and mentor when brand changes (for appointment screen only)
  useEffect(() => {
    if (selectedFlow === 'appointment-screen') {
      // Check if current location belongs to the selected brand
      const locationBelongsToBrand = locations?.some(
        (loc) => loc?.id === selectedLocationForOOH && loc?.clinicBrandId === selectedBrandForOOH,
      )
      // If location doesn't belong to brand (or brand is empty), clear location and mentor
      if (!locationBelongsToBrand && selectedBrandForOOH) {
        setSelectedLocationForOOH('')
        setSelectedMentorForOOH('')
      }
    }
  }, [selectedBrandForOOH, selectedFlow])

  // Clear mentor when location changes (for appointment screen only)
  useEffect(() => {
    if (selectedFlow === 'appointment-screen' && selectedLocationForOOH) {
      // Check if current mentor is available for the selected location using mentorLocations from API 3
      const mentorLocationIds = mentorLocations[selectedMentorForOOH] || []
      const mentorAvailableForLocation = mentorLocationIds.includes(selectedLocationForOOH)
      // If mentor is not available for location, clear mentor
      if (!mentorAvailableForLocation) {
        setSelectedMentorForOOH('')
      }
    }
  }, [selectedLocationForOOH, selectedFlow, selectedMentorForOOH, mentorLocations])

  // Auto-prefill when a dropdown has exactly one option (fewest clicks for the
  // operator). Each effect self-guards on the relevant portal's data, so the
  // display effects no-op in the Kiosk portal and vice-versa.
  // Display portal: brand -> location -> mentor.
  useEffect(() => {
    if (selectedFlow !== 'appointment-screen' || selectedBrandForOOH) return
    if (brands?.length === 1 && brands[0]?.clinicBrandId) {
      setSelectedBrandForOOH(brands[0].clinicBrandId)
    }
  }, [brands, selectedFlow, selectedBrandForOOH])

  useEffect(() => {
    if (selectedFlow !== 'appointment-screen' || !selectedBrandForOOH || selectedLocationForOOH) return
    const inBrand = locations?.filter((loc) => loc?.clinicBrandId === selectedBrandForOOH) || []
    if (inBrand.length === 1 && inBrand[0]?.id) {
      setSelectedLocationForOOH(inBrand[0].id)
    }
  }, [locations, selectedFlow, selectedBrandForOOH, selectedLocationForOOH])

  useEffect(() => {
    if (selectedFlow !== 'appointment-screen' || !selectedLocationForOOH || selectedMentorForOOH) return
    const available = (mentors || []).filter((m) =>
      (mentorLocations[m?.id] || []).includes(selectedLocationForOOH),
    )
    const uniqueIds = Array.from(new Set(available.map((m) => m?.id).filter(Boolean)))
    if (uniqueIds.length === 1) {
      setSelectedMentorForOOH(uniqueIds[0])
    }
  }, [mentors, mentorLocations, selectedFlow, selectedLocationForOOH, selectedMentorForOOH])

  // Kiosk portal: screen device config -> chamber room.
  useEffect(() => {
    if (selectedFlow !== 'remote-doctor' || selectedScreenConfigForRemoteScreen) return
    if (screenConfigs?.length === 1 && screenConfigs[0]?.id) {
      setSelectedScreenConfigForRemoteScreen(screenConfigs[0].id)
    }
  }, [screenConfigs, selectedFlow, selectedScreenConfigForRemoteScreen])

  useEffect(() => {
    if (selectedFlow !== 'remote-doctor' || !selectedScreenConfigForRemoteScreen || selectedCurrentVirtualRoomId)
      return
    const rooms = (virtualConfigs || []).filter(
      (v) => v?.screenDeviceConfigId === selectedScreenConfigForRemoteScreen,
    )
    if (rooms.length === 1 && rooms[0]?.virtualChamberRoomId) {
      setSelectedCurrentVirtualRoomId(rooms[0].virtualChamberRoomId)
    }
  }, [virtualConfigs, selectedFlow, selectedScreenConfigForRemoteScreen, selectedCurrentVirtualRoomId])

  // Helper functions for getting slugs
  const getLocationSlug = (locationId) => {
    const location = locations?.find((l) => l?.id === locationId)
    return location?.slug || locationId
  }

  const getMentorSlug = (mentorId) => {
    const mentor = mentors?.find((m) => m?.id === mentorId)
    // Check both slug and uid (some APIs return slug as uid)
    return mentor?.slug || mentor?.uid || mentorId
  }

  // Get profile data
  const profileData = useMemo(() => {
    const userProfile = assistantData?.userProfileData
    const profilePicId = userProfile?.profilePicId || userProfile?.profilePictureId
    const name = userProfile?.name || userProfile?.firstName || 'Kiosk User'

    let profilePicUrl = null
    if (profilePicId) {
      if (isValidHttpUrl(profilePicId)) {
        profilePicUrl = profilePicId
      } else {
        profilePicUrl = `${mediaURL}${profilePicId}`
      }
    }

    const getInitials = (nameStr) => {
      if (!nameStr || nameStr === 'Kiosk User') {
        return 'KU'
      }
      const words = nameStr.trim().split(/\s+/).filter((w) => w.length > 0)
      if (words.length > 1) {
        return (words[0][0] + words[words.length - 1][0]).toUpperCase()
      } else if (words.length === 1 && words[0].length > 0) {
        return words[0].substring(0, 2).toUpperCase()
      }
      return 'KU'
    }

    return {
      name,
      profilePicUrl,
      initials: getInitials(name),
    }
  }, [assistantData])

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLeave = () => {
    // Route to the configured screen for this portal (target-presence based;
    // no stored flow field).
    if (isDisplayPortalPath() && kioskConfig?.LOCATION_FOR_OOH && kioskConfig?.MENTOR_FOR_OOH) {
      const locationSlug = locations?.find((l) => l?.id === kioskConfig?.LOCATION_FOR_OOH)?.slug || kioskConfig?.LOCATION_FOR_OOH
      const mentorSlug = mentors?.find((m) => m?.id === kioskConfig?.MENTOR_FOR_OOH)?.slug || kioskConfig?.MENTOR_FOR_OOH
      navigate(`/display/${locationSlug}/${mentorSlug}`)
    } else if (!isDisplayPortalPath() && kioskConfig?.SCREEN_DEVICE_CONFIG_ID && kioskConfig?.CURRENT_VIRTUAL_ROOM_ID) {
      navigate('/kiosk/chamber-screen-home')
    } else {
      navigate(isDisplayPortalPath() ? '/display' : '/kiosk')
    }
  }

  const handleChange = () => {
    // Show flow selection so user can change flow type
    setShowFlowSelection(true)
    setShowConfigForm(false)
    // The selectedFlow is already set from the current config, so it will be pre-selected
  }


  const handleBackToSelection = () => {
    setShowConfigForm(false)
    setShowFlowSelection(false)
  }

  const handleContinue = async () => {
    try {
      // Validate form before proceeding
      if (!isFormValid()) {
        console.error('Form validation failed')
        return
      }

      const newKioskConfig = { ...kioskConfig }
      let additionalData = {}
      let redirectUrl = `${window.location.origin}/kiosk`

      switch (selectedFlow) {
        case 'appointment-screen':
          Object.assign(newKioskConfig, {
            BRAND_FOR_OOH: selectedBrandForOOH,
            LOCATION_FOR_OOH: selectedLocationForOOH,
            MENTOR_FOR_OOH: selectedMentorForOOH,
          })
          additionalData = {
            clinicBrandId: selectedBrandForOOH,
            clinicLocationId: selectedLocationForOOH,
          }
          // Persist the OOH target server-side (source of truth + write-through
          // cache); localStorage below stays only as the cache.
          try {
            await saveDisplayConfigToServer({
              clinicLocationId: selectedLocationForOOH,
              mentorId: selectedMentorForOOH,
            })
          } catch (e) {
            console.error('Failed to persist display screen config to server:', e)
          }
          // Use slug-based URL for cleaner routing. The appointment/token screen
          // lives in the Display Portal (/display); the legacy /kiosk/display
          // path is no longer used.
          const locationSlug = getLocationSlug(selectedLocationForOOH)
          const mentorSlug = getMentorSlug(selectedMentorForOOH)
          redirectUrl = `${window.location.origin}/display/${locationSlug}/${mentorSlug}`
          break

        case 'remote-doctor':
          Object.assign(newKioskConfig, {
            SCREEN_DEVICE_CONFIG_ID: selectedScreenConfigForRemoteScreen,
            CURRENT_VIRTUAL_ROOM_ID: selectedCurrentVirtualRoomId,
          })
          // Persist the chamber target server-side (source of truth +
          // write-through cache); localStorage below stays only as the cache.
          try {
            await saveKioskConfigToServer({
              screenDeviceConfigId: selectedScreenConfigForRemoteScreen,
              virtualChamberRoomId: selectedCurrentVirtualRoomId,
            })
          } catch (e) {
            console.error('Failed to persist kiosk screen config to server:', e)
          }

          const selectedVirtualRoom = assistantData?.associatedChamberRoomsData?.find(
            (room) => room?.id === selectedCurrentVirtualRoomId,
          )
          const clinicLocation = assistantData?.associatedClinicsData?.find(
            (clinic) => clinic?.id === selectedVirtualRoom?.clinicLocationId,
          )

          additionalData = {
            screenDeviceConfigId: selectedScreenConfigForRemoteScreen,
            chamberRoomId: selectedCurrentVirtualRoomId,
            clinicLocationId: selectedVirtualRoom?.clinicLocationId,
            clinicBrandId: clinicLocation?.clinicBrandId,
          }
          redirectUrl = `${window.location.origin}/kiosk/chamber-screen-home`
          break
        default:
          break
      }

      // Save to kiosk-config (primary for kiosk flows)
      localStorage.setItem('kiosk-config', JSON.stringify(newKioskConfig))
      // ⚠️ TEMPORARY: Also update ROLE_CA_CONFIG for backward compatibility during migration
      // This allows existing users who only have ROLE_CA_CONFIG to continue working
      // TODO: Remove this after all existing users are migrated (check usage metrics)
      localStorage.setItem('ROLE_CA_CONFIG', JSON.stringify(newKioskConfig))

      setIsRegisteringScreen(true)
      if (selectedFlow === 'remote-doctor') {
        try {
          console.log('Registering from selection')
          const result = await registerScreen(selectedFlow, additionalData)

          if (result.success) {
            console.log('Screen registered successfully:', result.screenId)
          } else {
            console.error('Failed to register screen:', result.error)
          }
        } catch (error) {
          console.error('Error during API calls:', error)
        }
      } else {
        try {
          await unregisterScreen()
        } catch (error) {
          console.error('Error unregistering screen:', error)
        }
      }
      setIsRegisteringScreen(false)
      window.location.href = redirectUrl
    } catch (error) {
      console.error('Error in handleContinue:', error)
      setIsRegisteringScreen(false)
      // Optionally show an error message to the user
      alert('An error occurred while saving the configuration. Please try again.')
    }
  }

  const isFormValid = () => {
    switch (selectedFlow) {
      case 'appointment-screen':
        return selectedBrandForOOH && selectedLocationForOOH && selectedMentorForOOH
      case 'remote-doctor':
        return selectedScreenConfigForRemoteScreen && selectedCurrentVirtualRoomId
      default:
        return false
    }
  }

  return (
    <Box sx={{ minHeight: '100vh', background: '#f9fafb' }}>
      {/* Header */}
      <HeaderContainer>
        <LogoContainer onClick={() => navigate('/kiosk')}>
          <img src={APP_CONSTANTS.APP_LOGO_LINK} alt="AUMHUM" />
        </LogoContainer>

        <HeaderRight>
          <PortalSwitcherButton />

          <Tooltip title={profileData.name} arrow>
            <ProfileButton onClick={handleClick} aria-label="Account menu">
              <StyledAvatar src={profileData.profilePicUrl || undefined}>
                {profileData.initials}
              </StyledAvatar>
            </ProfileButton>
          </Tooltip>

          <StyledMenu
            anchorEl={anchorEl}
            id="account-menu"
            open={open}
            onClose={handleClose}
            onClick={handleClose}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItemStyled
              onClick={() => {
                logoutHandlerMentor()
                handleClose()
              }}
            >
              <Logout sx={{ fontSize: 18, color: '#ef4444', mr: 1.5 }} />
              <Typography sx={{ color: '#ef4444', fontWeight: 600 }}>Logout</Typography>
            </MenuItemStyled>
          </StyledMenu>
        </HeaderRight>
      </HeaderContainer>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box
          sx={{
            background: 'white',
            borderRadius: '16px',
            padding: '32px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600, marginBottom: 1 }}>
            Configure Kiosk
          </Typography>
          <Typography variant="body2" sx={{ color: '#6b7280', marginBottom: 4 }}>
            Select the type of screen you want to configure
          </Typography>

          {hasValidConfig && !showConfigForm && !showFlowSelection && (
            <CurrentConfigCard
              config={kioskConfig}
              brands={brands}
              locations={locations}
              mentors={mentors}
              screenConfigs={screenConfigs}
              virtualConfigs={virtualConfigs}
              onChange={handleChange}
              onLeave={handleLeave}
            />
          )}

          {/* One flow per portal — no flow selector. Show the config form
              directly when there's no valid config, or when editing. */}
          {(showConfigForm || !hasValidConfig || showFlowSelection) && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 3 }}>
                <Button
                  variant="text"
                  onClick={handleBackToSelection}
                  sx={{
                    minWidth: 'auto',
                    padding: '8px 16px',
                    color: '#6b7280',
                    '&:hover': {
                      background: 'rgba(116, 80, 170, 0.05)',
                    },
                  }}
                >
                  ← Back
                </Button>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1f2937' }}>
                  Configure {selectedFlow === 'appointment-screen' ? 'Appointment Screen' : 'Video Call Screen'}
                </Typography>
              </Box>

              {selectedFlow === 'appointment-screen' && (
                <AppointmentScreenConfig
                  loading={oohDataLoading}
                  selectedBrand={selectedBrandForOOH}
                  selectedLocation={selectedLocationForOOH}
                  selectedMentor={selectedMentorForOOH}
                  onBrandChange={setSelectedBrandForOOH}
                  onLocationChange={setSelectedLocationForOOH}
                  onMentorChange={setSelectedMentorForOOH}
                  brands={brands}
                  locations={locations}
                  mentors={mentors}
                  mentorLocations={mentorLocations}
                />
              )}

              {selectedFlow === 'remote-doctor' && (
                <RemoteDoctorConfig
                  loading={screenConfigLoading}
                  selectedScreenConfig={selectedScreenConfigForRemoteScreen}
                  selectedVirtualRoom={selectedCurrentVirtualRoomId}
                  onScreenConfigChange={setSelectedScreenConfigForRemoteScreen}
                  onVirtualRoomChange={setSelectedCurrentVirtualRoomId}
                  screenConfigs={screenConfigs}
                  virtualConfigs={virtualConfigs}
                />
              )}

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, marginTop: 4 }}>
                <Button
                  variant="outlined"
                  onClick={handleBackToSelection}
                  sx={{
                    borderRadius: '12px',
                    padding: '12px 24px',
                    textTransform: 'none',
                    fontWeight: 600,
                    borderColor: '#d1d5db',
                    color: '#6b7280',
                    '&:hover': {
                      borderColor: '#9ca3af',
                      background: 'rgba(0, 0, 0, 0.02)',
                    },
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disabled={!isFormValid() || isRegisteringScreen}
                  onClick={handleContinue}
                  sx={{
                    borderRadius: '12px',
                    padding: '12px 32px',
                    textTransform: 'none',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #7450aa 0%, #5a3d88 100%)',
                    boxShadow: '0 4px 12px rgba(116, 80, 170, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5a3d88 0%, #4a2d6a 100%)',
                      boxShadow: '0 6px 16px rgba(116, 80, 170, 0.4)',
                    },
                    '&:disabled': {
                      background: '#e5e7eb',
                      color: '#9ca3af',
                    },
                  }}
                >
                  {isRegisteringScreen ? 'Configuring...' : 'Save Configuration'}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Container>
    </Box>
  )
}
